<div class="p-4">
  <div class="block sm:inline-block w-full sm:w-auto">
    <div class="w-full sm:max-w-3xl">

      <!-- Instructions -->
      <div class="mb-4 text-sm text-gray-500 flex flex-col gap-1 max-w-xl">
        <span>Sends a re-engagement email to consumer referrals created within the date range below.</span>
        <span><strong class="text-agentium_gray-text">Preview</strong> shows how many referrals would be emailed — nothing is sent or queued. <strong class="text-agentium_gray-text">Send</strong> asks you to confirm the count, then queues them for sending — delivery happens shortly after, in the background, and won't show a live sent/failed count here.</span>
      </div>

      <!-- Automation -->
      <div class="rounded-lg border border-gray-200 bg-white p-5 mb-4 w-full">
        <div class="flex items-center justify-between">
          <div>
            <div class="font-medium text-agentium_gray-text">Automated follow-up</div>
            <div class="text-sm text-gray-500">
              Runs daily on its own schedule using the same eligibility rules as the manual tool.
            </div>
          </div>
          <div class="flex items-center gap-3">
            @if (isSavingAutomation) {
              <fa-icon [icon]="faSpinner" class="animate-spin text-slate-500"></fa-icon>
            }
            <div
              role="switch"
              class="relative inline-block w-10 h-6 rounded-full cursor-pointer"
              [attr.aria-checked]="isAutomationEnabled"
              tabindex="0"
              (click)="toggleAutomationEnabled()"
              (keydown.space)="toggleAutomationEnabled(); $event.preventDefault()"
            >
              <div
                class="w-full h-full rounded-full transition-colors duration-200 ease-in-out"
                [ngClass]="isAutomationEnabled ? 'bg-momentum-deep_purple' : 'bg-gray-300'"
              ></div>
              <div
                class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transform transition-transform duration-200 ease-in-out"
                [ngClass]="isAutomationEnabled ? 'translate-x-4' : 'translate-x-0'"
              ></div>
            </div>
          </div>
        </div>

        @if (isAutomationEnabled) {
          <div class="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            Automation is active — manual Preview/Send below is disabled. Turn automation off to run the campaign manually.
          </div>
        }

        <div class="flex flex-col sm:flex-row sm:items-end gap-4 mt-4 pt-4 border-t border-gray-100">
          <div>
            <label class="block text-sm font-medium text-agentium_gray-text mb-1">Cadence</label>
            <div class="flex items-center gap-4 h-10">
              <label class="flex items-center gap-1 text-sm text-agentium_gray-text">
                <input type="radio" name="cadenceMode" value="ANNUAL" [(ngModel)]="automationCadenceMode" />
                Annual
              </label>
              <label class="flex items-center gap-1 text-sm text-agentium_gray-text">
                <input type="radio" name="cadenceMode" value="MONTHLY" [(ngModel)]="automationCadenceMode" />
                Monthly
              </label>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-agentium_gray-text mb-1" for="yearsBack">
              Years Back
            </label>
            <input
              id="yearsBack"
              type="number"
              min="1"
              max="5"
              [(ngModel)]="automationYearsBack"
              class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-24 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            />
            @if (yearsBackError) {
              <div class="mt-1 text-sm text-red-600">{{ yearsBackError }}</div>
            }
          </div>

          <div>
            <label class="block text-sm font-medium text-agentium_gray-text mb-1" for="runTime">
              Run Time <span class="text-gray-400 font-normal">(America/New_York)</span>
            </label>
            <input
              id="runTime"
              type="time"
              [(ngModel)]="automationRunTime"
              class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-32 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            />
          </div>
        </div>

        <div class="flex flex-col sm:flex-row sm:items-end gap-4 mt-4">
          <div>
            <label class="block text-sm font-medium text-agentium_gray-text mb-1" for="automationCategory">
              Category <span class="text-gray-400 font-normal">(optional)</span>
            </label>
            <select
              id="automationCategory"
              [(ngModel)]="automationCategory"
              class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            >
              <option value="">All Categories</option>
              @for (option of categoryOptions; track option.value) {
                <option [value]="option.value">{{ option.label }}</option>
              }
            </select>
          </div>

          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="block text-sm font-medium text-agentium_gray-text" for="automationLobCodes">
                LOB <span class="text-gray-400 font-normal">(optional, multi-select)</span>
              </label>
              @if (automationLobCodes.length > 0) {
                <button
                  type="button"
                  class="text-xs text-momentum-deep_purple hover:underline"
                  (click)="automationLobCodes = []"
                >
                  Clear ({{ automationLobCodes.length }})
                </button>
              }
            </div>
            <select
              id="automationLobCodes"
              multiple
              [(ngModel)]="automationLobCodes"
              class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-80 h-24 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            >
              @for (option of lobOptions; track option.code) {
                <option [value]="option.code">{{ option.name }}</option>
              }
            </select>
          </div>

          <button
            class="bg-momentum-deep_purple text-white px-4 py-2 rounded-full flex items-center justify-center gap-2 hover:bg-momentum-royal_purple transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            [disabled]="isSavingAutomation || isLoadingAutomation || !!yearsBackError"
            (click)="saveAutomationSettings()"
          >
            Save Automation Settings
          </button>
        </div>

        @if (automationSettings?.lastRunDate) {
          <div class="mt-3 text-xs text-gray-500">
            Last automated run: {{ automationSettings?.lastRunDate | date: "mediumDate" }} —
            {{ automationSettings?.lastRunQueuedCount ?? 0 }} queued,
            {{ automationSettings?.lastRunSkippedCount ?? 0 }} skipped.
          </div>
        }
      </div>

      <!-- Controls -->
      <div class="rounded-lg border border-gray-200 bg-white p-5 mb-4 w-full">
        <div class="flex flex-col sm:flex-row sm:items-end gap-4">
          <div>
            <label class="block text-sm font-medium text-agentium_gray-text mb-1" for="fromDate">
              From Date
            </label>
            <input
              id="fromDate"
              type="date"
              [(ngModel)]="fromDate"
              [max]="maxDate"
              class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-agentium_gray-text mb-1" for="toDate">
              To Date
            </label>
            <input
              id="toDate"
              type="date"
              [(ngModel)]="toDate"
              [max]="maxDate"
              class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-agentium_gray-text mb-1" for="limit">
              Limit <span class="text-gray-400 font-normal">(test only)</span>
            </label>
            <input
              id="limit"
              type="number"
              min="1"
              placeholder="No limit"
              [(ngModel)]="limit"
              class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-32 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            />
          </div>

          <button
            class="bg-momentum-deep_purple text-white px-4 py-2 rounded-full flex items-center justify-center gap-2 hover:bg-momentum-royal_purple transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            [disabled]="!fromDate || !toDate || !!dateRangeError || isPreviewing || isSending || isAutomationEnabled"
            (click)="preview()"
          >
            @if (isPreviewing) {
              <fa-icon [icon]="faSpinner" class="animate-spin"></fa-icon>
            } @else {
              <fa-icon [icon]="faEye"></fa-icon>
            }
            Preview
          </button>
        </div>
        @if (dateRangeError) {
          <div class="mt-2 text-sm text-red-600">{{ dateRangeError }}</div>
        }

        <!-- Category / LOB filters -->
        <div class="flex flex-col sm:flex-row sm:items-end gap-4 mt-4 pt-4 border-t border-gray-100">
          <div>
            <label class="block text-sm font-medium text-agentium_gray-text mb-1" for="category">
              Category <span class="text-gray-400 font-normal">(optional)</span>
            </label>
            <select
              id="category"
              [(ngModel)]="selectedCategory"
              class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            >
              <option value="">All Categories</option>
              @for (option of categoryOptions; track option.value) {
                <option [value]="option.value">{{ option.label }}</option>
              }
            </select>
          </div>

          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="block text-sm font-medium text-agentium_gray-text" for="lobCodes">
                LOB <span class="text-gray-400 font-normal">(optional, multi-select)</span>
              </label>
              @if (selectedLobCodes.length > 0) {
                <button
                  type="button"
                  class="text-xs text-momentum-deep_purple hover:underline"
                  (click)="selectedLobCodes = []"
                >
                  Clear ({{ selectedLobCodes.length }})
                </button>
              }
            </div>
            <select
              id="lobCodes"
              multiple
              [(ngModel)]="selectedLobCodes"
              class="border rounded p-2 text-agentium_gray-text border-gray-300 w-full sm:w-80 h-32 focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            >
              @for (option of lobOptions; track option.code) {
                <option [value]="option.code">{{ option.name }}</option>
              }
            </select>
          </div>
        </div>
      </div>

      <!-- Preview result -->
      @if (previewResult) {
        <div class="rounded-lg border border-gray-200 bg-white p-5 mb-4 w-full sm:w-fit">
          <div class="flex items-center gap-3 mb-1">
            <fa-icon [icon]="faEnvelope" class="text-momentum-deep_purple" size="lg"></fa-icon>
            <span class="text-agentium_gray-text">
              <strong class="text-2xl">{{ previewResult.eligibleCount }}</strong>
              referral(s) created
              @if (fromDate === toDate) {
                on {{ fromDate | date: "mediumDate" }}
              } @else {
                {{ fromDate | date: "mediumDate" }} through {{ toDate | date: "mediumDate" }}
              }
              would receive an email.
            </span>
          </div>
          @if (previewResult.skipped > 0) {
            <div class="mt-2 text-xs text-gray-500">
              {{ previewResult.skipped }} referral(s) were excluded (duplicates, opt-outs, or other eligibility rules).
            </div>
          }
          <div class="mt-4">
            <button
              class="bg-momentum-deep_purple text-white px-4 py-2 rounded-full flex items-center justify-center gap-2 hover:bg-momentum-royal_purple transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              [disabled]="previewResult.eligibleCount === 0 || isSending"
              (click)="confirmSend()"
            >
              @if (isSending) {
                <fa-icon [icon]="faSpinner" class="animate-spin"></fa-icon>
              } @else {
                <fa-icon [icon]="faPaperPlane"></fa-icon>
              }
              Send to {{ previewResult.eligibleCount }} referral(s)
            </button>
          </div>
        </div>
      }

      <!-- Send result — queued count only; this campaign sends asynchronously, so there is no
           live sent/failed count to show here (see opportunity_email_notification_state for
           actual delivery outcomes). -->
      @if (sendResult) {
        <div class="rounded-lg border border-green-200 bg-green-50 p-5 w-full sm:w-fit">
          <div class="text-agentium_gray-text font-semibold mb-2">Campaign queued</div>
          <div class="flex gap-6 text-sm">
            <span class="text-green-700"><strong>{{ sendResult.queued }}</strong> queued</span>
            <span class="text-gray-600"><strong>{{ sendResult.skipped }}</strong> skipped</span>
          </div>
          <div class="mt-2 text-xs text-gray-500">
            Sending happens in the background shortly — this screen won't show a live sent/failed
            count.
          </div>
          @if (sendResult.skipped > 0) {
            <div class="mt-2 text-xs text-gray-500">
              {{ sendResult.skipped }} referral(s) were excluded (duplicates, opt-outs, or other eligibility rules).
            </div>
          }
        </div>
      }

    </div>
  </div>
</div>
